/**
 * The spelling rules behind human-readable document numbers: which placeholders a
 * number-series pattern may use, how a pattern renders, and how a rendered number reads back.
 */
import { escapeRegExp } from 'es-toolkit';

/** Placeholder names a pattern may use, in the order the settings UI offers them. */
export const NUMBER_SERIES_PLACEHOLDERS = ['prefix', 'year', 'yy', 'month', 'seq'] as const;

export type NumberSeriesPlaceholder = (typeof NUMBER_SERIES_PLACEHOLDERS)[number];

/** Matches one `{...}` token; the inner group is the placeholder name. */
const PLACEHOLDER_TOKEN = /\{([^{}]*)\}/g;

/** Bounds mirrored from the `number_series` columns. */
export const NUMBER_SERIES_PATTERN_MAX_LENGTH = 50;
export const NUMBER_SERIES_PREFIX_MAX_LENGTH = 20;
export const NUMBER_SERIES_PADDING_MIN = 1;
export const NUMBER_SERIES_PADDING_MAX = 10;

/** Width of the document `number` column a rendered number has to fit into. */
export const NUMBER_SERIES_NUMBER_MAX_LENGTH = 50;

/** Years a series may be configured for; keeps a caller from seeding arbitrary rows. */
export const NUMBER_SERIES_YEAR_MIN = 2000;
export const NUMBER_SERIES_YEAR_MAX = 2100;

/** Why a pattern was rejected. Codes are stable so the UI can translate them. */
export const NumberSeriesPatternIssueCode = {
  /** Pattern is empty or only whitespace. */
  EMPTY: 'EMPTY',
  /** Pattern is longer than the column allows. */
  TOO_LONG: 'TOO_LONG',
  /** A `{...}` token no formatter understands — typically a typo like `{yaer}`. */
  UNKNOWN_PLACEHOLDER: 'UNKNOWN_PLACEHOLDER',
  /** A stray `{` or `}` that is not part of a placeholder. */
  UNBALANCED_BRACE: 'UNBALANCED_BRACE',
  /** No `{seq}`, so every document would be numbered identically. */
  MISSING_SEQUENCE: 'MISSING_SEQUENCE',
  /** Prefix is empty or only whitespace. */
  PREFIX_EMPTY: 'PREFIX_EMPTY',
  /** Prefix is longer than the column allows. */
  PREFIX_TOO_LONG: 'PREFIX_TOO_LONG',
  /** Padding is outside the accepted digit width. */
  PADDING_OUT_OF_RANGE: 'PADDING_OUT_OF_RANGE',
  /** The rendered number would not fit the document `number` column. */
  RENDERED_TOO_LONG: 'RENDERED_TOO_LONG'
} as const;

export type NumberSeriesPatternIssueCode =
  (typeof NumberSeriesPatternIssueCode)[keyof typeof NumberSeriesPatternIssueCode];

/** A single reason a pattern is unusable. */
export interface NumberSeriesPatternIssue {
  code: NumberSeriesPatternIssueCode;
  /** The offending token, for the issues that are about one. */
  placeholder?: string;
}

function isKnownPlaceholder(name: string): name is NumberSeriesPlaceholder {
  return (NUMBER_SERIES_PLACEHOLDERS as readonly string[]).includes(name);
}

/**
 * Every reason `pattern` cannot be used, empty when it is valid. A pattern without `{seq}`
 * renders the same number for every document, which the unique index turns into a failed
 * document creation rather than a visibly wrong number.
 */
export function validateNumberSeriesPattern(pattern: string): NumberSeriesPatternIssue[] {
  if (pattern.trim() === '') {
    return [{ code: NumberSeriesPatternIssueCode.EMPTY }];
  }

  const issues: NumberSeriesPatternIssue[] = [];
  if (pattern.length > NUMBER_SERIES_PATTERN_MAX_LENGTH) {
    issues.push({ code: NumberSeriesPatternIssueCode.TOO_LONG });
  }

  const seen = new Set<string>();
  const literal = pattern.replace(PLACEHOLDER_TOKEN, (token, name: string) => {
    if (isKnownPlaceholder(name)) {
      seen.add(name);
    } else if (!seen.has(token)) {
      seen.add(token);
      issues.push({ code: NumberSeriesPatternIssueCode.UNKNOWN_PLACEHOLDER, placeholder: token });
    }
    return '';
  });

  if (literal.includes('{') || literal.includes('}')) {
    issues.push({ code: NumberSeriesPatternIssueCode.UNBALANCED_BRACE });
  }
  if (!seen.has('seq')) {
    issues.push({ code: NumberSeriesPatternIssueCode.MISSING_SEQUENCE });
  }

  return issues;
}

/** Whether `pattern` can be saved — convenience over `validateNumberSeriesPattern`. */
export function isValidNumberSeriesPattern(pattern: string): boolean {
  return validateNumberSeriesPattern(pattern).length === 0;
}

/** The editable configuration of one series. */
export interface NumberSeriesConfig {
  pattern: string;
  prefix: string;
  padding: number;
  /** Next sequence to be issued; a value wider than `padding` renders wider than it. */
  nextValue?: number;
}

/**
 * Every reason a whole series configuration cannot be saved, empty when it is valid.
 * Checks the rendered result too: a pattern short enough to store can still expand past the
 * document `number` column, which would fail document creation rather than the save.
 */
export function validateNumberSeriesConfig(config: NumberSeriesConfig): NumberSeriesPatternIssue[] {
  const { pattern, prefix, padding } = config;
  const issues = validateNumberSeriesPattern(pattern);

  if (prefix.trim() === '') {
    issues.push({ code: NumberSeriesPatternIssueCode.PREFIX_EMPTY });
  }
  if (prefix.length > NUMBER_SERIES_PREFIX_MAX_LENGTH) {
    issues.push({ code: NumberSeriesPatternIssueCode.PREFIX_TOO_LONG });
  }
  if (
    !Number.isInteger(padding) ||
    padding < NUMBER_SERIES_PADDING_MIN ||
    padding > NUMBER_SERIES_PADDING_MAX
  ) {
    issues.push({ code: NumberSeriesPatternIssueCode.PADDING_OUT_OF_RANGE });
  }

  if (issues.length === 0 && renderedWidth(config) > NUMBER_SERIES_NUMBER_MAX_LENGTH) {
    issues.push({ code: NumberSeriesPatternIssueCode.RENDERED_TOO_LONG });
  }

  return issues;
}

/** Whether a whole series configuration can be saved. */
export function isValidNumberSeriesConfig(config: NumberSeriesConfig): boolean {
  return validateNumberSeriesConfig(config).length === 0;
}

/**
 * Longest number the configuration can render. `{year}` is four digits and `{yy}`/`{month}` two
 * for every supported year, so only the sequence varies: `padStart` never truncates, so a
 * `nextValue` wider than `padding` renders wider than the padding alone would suggest.
 */
function renderedWidth(config: NumberSeriesConfig): number {
  const widest = Math.max(config.padding, String(config.nextValue ?? 1).length);

  return formatDocumentNumber({
    pattern: config.pattern,
    prefix: config.prefix,
    year: 2026,
    month: 12,
    padding: widest,
    sequence: 0
  }).length;
}

/** Everything a pattern needs to render one document number. */
export interface DocumentNumberFormatParams {
  pattern: string;
  prefix: string;
  year?: number | null;
  /** Calendar month 1-12, rendered by `{month}`. */
  month?: number | null;
  padding: number;
  sequence: number;
}

/**
 * Renders a document number from a series pattern, substituting **every** occurrence of each
 * placeholder. A placeholder with no value to render is left verbatim.
 */
export function formatDocumentNumber(params: DocumentNumberFormatParams): string {
  const { pattern, prefix, year, month, padding, sequence } = params;

  return pattern.replace(PLACEHOLDER_TOKEN, (token, name: string) => {
    switch (name) {
      case 'prefix':
        return prefix;
      case 'seq':
        return String(sequence).padStart(padding, '0');
      case 'year':
        return year == null ? token : String(year);
      case 'yy':
        return year == null ? token : String(year % 100).padStart(2, '0');
      case 'month':
        return month == null ? token : String(month).padStart(2, '0');
      default:
        return token;
    }
  });
}

/** The part of a series that decides how its numbers are spelled. */
export interface DocumentNumberFormat {
  pattern: string;
  prefix: string;
}

/** What a document number says about itself; `null` where the pattern has no such placeholder. */
export interface ParsedDocumentNumber {
  year: number | null;
  /** Two-digit year from `{yy}`; resolve it with `resolveShortYear`. */
  yearShort: number | null;
  month: number | null;
  sequence: number | null;
}

/** The full year a two-digit `{yy}` means, read in the century of `referenceYear`. */
export function resolveShortYear(yearShort: number, referenceYear: number): number {
  return Math.floor(referenceYear / 100) * 100 + yearShort;
}

/** Placeholders that become a capture group; `{prefix}` is substituted literally instead. */
type CapturingPlaceholder = Exclude<NumberSeriesPlaceholder, 'prefix'>;

const CAPTURE_GROUP: Record<CapturingPlaceholder, string> = {
  year: String.raw`(?<year>\d{4})`,
  yy: String.raw`(?<yy>\d{2})`,
  month: String.raw`(?<month>\d{2})`,
  seq: String.raw`(?<seq>\d+)`
};

function isCapturingPlaceholder(name: string): name is CapturingPlaceholder {
  return name !== 'prefix' && isKnownPlaceholder(name);
}

/**
 * Inverse of `formatDocumentNumber`: reads year, month and sequence out of a number, or `null`
 * when the number does not follow the pattern. A repeated placeholder must repeat the same
 * text, matching the formatter substituting every occurrence.
 */
export function parseDocumentNumber(
  format: DocumentNumberFormat,
  value: string
): ParsedDocumentNumber | null {
  const match = buildMatcher(format).exec(value);
  if (!match?.groups) return null;

  const asNumber = (raw: string | undefined): number | null =>
    raw === undefined ? null : Number(raw);
  const { year, yy, month, seq } = match.groups;

  return {
    year: asNumber(year),
    yearShort: asNumber(yy),
    month: asNumber(month),
    sequence: asNumber(seq)
  };
}

/** Builds the anchored matcher: literal segments escaped, placeholders turned into groups. */
function buildMatcher(format: DocumentNumberFormat): RegExp {
  const captured = new Set<string>();
  let source = '';
  let lastIndex = 0;

  for (const match of format.pattern.matchAll(PLACEHOLDER_TOKEN)) {
    const [token, name] = match;
    source += escapeRegExp(format.pattern.slice(lastIndex, match.index));
    lastIndex = match.index + token.length;

    if (name === 'prefix') {
      source += escapeRegExp(format.prefix);
    } else if (!isCapturingPlaceholder(name)) {
      source += escapeRegExp(token);
    } else if (captured.has(name)) {
      // The formatter writes the same text at every occurrence, so repeats are backreferences.
      source += String.raw`\k<${name}>`;
    } else {
      captured.add(name);
      source += CAPTURE_GROUP[name];
    }
  }

  source += escapeRegExp(format.pattern.slice(lastIndex));
  return new RegExp(`^${source}$`);
}
